import * as React from "react";
import { rng, PublicAmorgUser, userHelper, PermissionGroups } from '../../amorg-common';
import { ProtectedPageWrapper } from "../../designSystem/layouts/pageWrappers/ProtectedPageWrapper";
import { useState } from "react";
import { Table } from '../../designSystem/Table';
import { getAuthorizedActiveUserOrRedirect, hasUserPermissions } from '../../api/serverApiHelpers';
import CreateUserModalButton from "./_components/CreateUserModalButton";
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { UserToken } from '../../amorg-common';
import { LinkButton } from "../../designSystem/button/LinkButton";
import styled from "styled-components";
import { colors } from "../../designSystem/colors/color";
import { getUsers } from "../../api/userClientApi";
import { TextField } from "../../designSystem/formField/text/TextField";
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { SelectField } from "../../designSystem/formField/SelectField";
import { txt } from "../../txt";
import { PageHeader } from "../../designSystem/PageHeader";
import { OnOff } from "../../designSystem/inputs/OnOff";

export default function UserListingPage(): JSX.Element {
	const [activeUser, setActiveUser] = useState<UserToken | null>(null);
	const [users, setUsers] = useState<PublicAmorgUser[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedPermissionGroup, setSelectedPermissionGroup] = useState('');
    const [showDisabledUsers, setShowDisabledUsers] = useState(false);

	React.useEffect(() => {
		const getData = async () => {
			const result = await getAuthorizedActiveUserOrRedirect(['ViewUsers']);
			setActiveUser(result);
            await updateUsers();
		}
		getData();
	},[]);

    async function updateUsers()
    {
        setUsers((await getUsers(true, true)).sort((lhs,rhs) =>
            userHelper.getPublicUserFullName(lhs).localeCompare(userHelper.getPublicUserFullName(rhs))
    ));
    }

	async function handleUserCreated() {
		await updateUsers();
	}

	const isAdmin = activeUser && hasUserPermissions(activeUser, ['UserAdmin']);
	const includeSuperAdmins = activeUser !== null && activeUser.permissionGroupId === 'superadmin';
	const filteredUsers = users
        .filter(u => showDisabledUsers ? true : u.enabled)
        .filter(u => includeSuperAdmins || u.permissionGroup.id !== 'superadmin')
        .filter(u => searchText==='' 
            ? true 
            : (
                userHelper.getPublicUserFullName(u).toLowerCase().includes(searchText.toLowerCase()) ||
                u.email.toLowerCase().includes(searchText.toLowerCase())
            ))
        .filter(u => selectedRole==='' || u.currentRole?.title === selectedRole)
        .filter(u => selectedPermissionGroup==='' || u.permissionGroup.id === selectedPermissionGroup);

	return (
		<ProtectedPageWrapper requiredPermissions={['ViewUsers']} selectedMenuItemKey='users'>
			<SingleColumnLayout
                noDivider
				contentContainer={
                    <>
                        <PageHeader 
                            headerIcon='interface_user_users'
                            headerText="Brugere" 
                            headerStyle="secondary-gray"
                            buttons={
                                <>
                                    {activeUser && isAdmin &&
                                        <CreateUserModalButton onUserCreated={handleUserCreated} />
                                    }
                                </>
                            } />

                        <Flex alignItems="flex-start" justifyContent="space-between">
                            
                            <TextField 
                                value={searchText}
                                onChange={(value) => setSearchText(value)}
                                label="Søg"
                                trailingIcon="interface_actions_search"
                                validator={() => ({valid:true}) }
                                placeholder="Søg på navn og email" />

                            <Flex flexDirection="column" gap={8}>

                                <Flex gap={8}>
                                    <SelectField 
                                        selectedValue={selectedRole} 
                                        options={
                                            [
                                                'Alle',
                                                ...rng.getUniqueStrings(users.map(u => u.currentRole?.title || ''), { ignoreEmpty:true })
                                            ]
                                            .map(t => ({text:t, value:t}))
                                        } 
                                        onChange={(newValue) => newValue === 'Alle' ? setSelectedRole('') :  setSelectedRole(newValue)} 
                                        label="Filtrér rolle" />
                                    <SelectField 
                                        selectedValue={selectedPermissionGroup} 
                                        options={
                                            [
                                                {text:'Alle', value:''},
                                                { text:txt.PermissionGroupId_user, value:PermissionGroups.user.id},
                                                { text:txt.PermissionGroupId_member, value:PermissionGroups.member.id},
                                                { text:txt.PermissionGroupId_siteadmin, value:PermissionGroups.siteAdmin.id}
                                            ]
                                        } 
                                        onChange={(newValue) => setSelectedPermissionGroup(newValue)} 
                                        label="Filtrér rettighedsniveau" />
                                </Flex>
                            
                                <Flex justifyContent="flex-end" gap={8}>
                                    <span>Vis deaktiverede brugere</span>
                                    <OnOff text={{
                                        width: 24,
                                        active:'',
                                        inactive:'',
                                    }} active={showDisabledUsers} onClick={(active)=>{ setShowDisabledUsers(active) }} />
                                
                                </Flex>
                            </Flex>

                        </Flex>
                        

                        <div style={{padding:'10px 16px'}}>	   
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Navn | Email</th>
                                        <th>Rolle</th>
                                        <th>Rettighedsniveau</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 &&
                                        filteredUsers.map(u =>
                                            <tr key={u.id}>
                                                <td>{
                                                    <>
                                                        <Flex gap={4}>
                                                            <PrimaryCellText disabled={!u.enabled} lineThrough={!u.enabled}>
                                                                {userHelper.getPublicUserFullName(u)}
                                                            </PrimaryCellText>
                                                            {!u.enabled && 
                                                                <PrimaryCellText disabled={!u.enabled}>
                                                                    (deaktiveret)
                                                                </PrimaryCellText>
                                                            }
                                                        </Flex>
                                                        <SecondaryCellText>
                                                            {u.email}
                                                        </SecondaryCellText>
                                                    </>
                                                    }
                                                </td>
                                                <td style={{width:'300px'}}>
                                                    <PrimaryCellText disabled={!u.enabled}>
                                                        {u.currentRole?.title || ''}
                                                    </PrimaryCellText>
                                                </td>
                                                <td style={{width:'200px'}}>
                                                    <PrimaryCellText disabled={!u.enabled}>
                                                        {u.permissionGroup.name}
                                                    </PrimaryCellText>
                                                </td>
                                                <td style={{textAlign:'right', width:'50px'}}>
                                                    {activeUser && isAdmin && 
                                                        <LinkButton variant="text" icon="arrows_chevron_chevronForward"
                                                            href={`/users/${u.id}`} />
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </>
				}
			/>
		</ProtectedPageWrapper>
	);
}

const PrimaryCellText = styled.span<{disabled?:boolean, lineThrough?:boolean}>`
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.5px;
    text-decoration: ${props => props.lineThrough ? 'line-through' : 'none'};
    color:${props => props.disabled ? props.theme.colors.text.inactive : colors.text.normal};
`;
const SecondaryCellText = styled.div`
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.25px;
    color:${colors.text.normal};
`;